import { sharedProperty } from '../../hooks';
import * as yup from "yup";
import { Formik } from "formik";
import { Col, FloatingLabel, Form, Row, Container } from 'react-bootstrap';
import { Button, FormSectionDivider, FormSectionHeader, FormToggleLabel } from '../../components';
import { ProviderServiceModel } from '../../data/entities';
import { useState } from 'react';

interface ProviderServiceDetailsPageProps {
  dataItemState: sharedProperty<ProviderServiceModel>;
  showState: sharedProperty<boolean>;
  onSubmit: any;
}

export function ProviderServiceDetailsPage(props: Readonly<ProviderServiceDetailsPageProps>) {
  const [overrideDuration, setOverrideDuration] = useState<boolean>(props.dataItemState.value?.duration ? true : false);

  const defaultDuration = props.dataItemState.value.service?.defaultDuration;
  const defaultBufferDuration = props.dataItemState.value.service?.defaultBufferDuration;

  const validationSchema = yup.object().shape({
    duration: yup.number().when('overrideDuration', {
      is: true,
      then: (schema) => schema.required("Duration is required"),
      otherwise: (schema) => schema.nullable(),
    }),
    bufferDuration: yup.number().when('overrideDuration', {
      is: true,
      then: (schema) => schema.required("Buffer duration is required"),
      otherwise: (schema) => schema.nullable(),
    })
  });

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={{
        id: props.dataItemState.value.id,
        serviceId: props.dataItemState.value.serviceId,
        providerId: props.dataItemState.value.providerId,
        duration: props.dataItemState.value.duration,
        bufferDuration: props.dataItemState.value.bufferDuration,
        overrideDuration: overrideDuration,
      }}
      onSubmit={async (values) => {
        if (!overrideDuration) {
          values.duration = null;
          values.bufferDuration = null;
        }
        const providerService = {
          ...values
        } as ProviderServiceModel;
        props.onSubmit(providerService);
        props.showState.setter(false);
      }}
    >
      {({ values, handleSubmit, handleChange, setFieldValue, touched, errors }) => {
        return (
          <Form onSubmit={handleSubmit} className="h-100 d-flex flex-column justify-content-between py-3">
            <Container className="px-3">

              {/* Service defaults, for reference */}
              <FormSectionHeader>Service Defaults</FormSectionHeader>
              <Row className="mb-2">
                <Col xs={12}>
                  <Form.Label className="small text-body-secondary mb-1">Duration</Form.Label>
                  <Form.Control
                    plaintext
                    readOnly
                    className="fw-semibold p-0"
                    value={defaultDuration != null ? `${defaultDuration} min` : "—"}
                  />
                </Col>
                <Col xs={12}>
                  <Form.Label className="small text-body-secondary mb-1">Buffer Duration</Form.Label>
                  <Form.Control
                    plaintext
                    readOnly
                    className="fw-semibold p-0"
                    value={defaultBufferDuration != null ? `${defaultBufferDuration} min` : "—"}
                  />
                </Col>
              </Row>

              <FormSectionDivider />

              {/* Override */}
              <FormSectionHeader>Duration Override</FormSectionHeader>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <FormToggleLabel
                  className="me-3"
                  title="Override Service Duration"
                  description="Use a custom duration for this provider instead of the service default"
                />
                <div className="form-check form-switch fs-4 flex-shrink-0 ps-0">
                  <input
                    className="form-check-input ms-0 mt-0"
                    type="checkbox"
                    role="switch"
                    style={{ cursor: "pointer" }}
                    checked={overrideDuration}
                    onChange={() => {
                      const newValue = !overrideDuration;
                      setOverrideDuration(newValue);
                      setFieldValue('overrideDuration', newValue);
                      if (!newValue) {
                        setFieldValue('duration', values.duration);
                        setFieldValue('bufferDuration', values.bufferDuration);
                      }
                    }}
                  />
                </div>
              </div>
              {
                overrideDuration &&
                <div className="mt-3">
                  <Row>
                    <Col>
                      <Form.Group controlId="duration">
                        <FloatingLabel label="Duration (min)" className="mb-3">
                          <Form.Control
                            type="number"
                            name="duration"
                            placeholder="Duration"
                            value={values.duration || ''}
                            onChange={handleChange}
                            isInvalid={touched.duration && !!errors.duration}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.duration}
                          </Form.Control.Feedback>
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <Form.Group controlId="bufferDuration">
                        <FloatingLabel label="Buffer Duration (min)" className="mb-3">
                          <Form.Control
                            type="number"
                            name="bufferDuration"
                            placeholder="Buffer Duration"
                            value={values.bufferDuration || ''}
                            onChange={handleChange}
                            isInvalid={touched.bufferDuration && !!errors.bufferDuration}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.bufferDuration}
                          </Form.Control.Feedback>
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              }
            </Container>

            <Container className="pt-3 border-top">
              <Row className="g-0">
                <div className="col-12 pe-2">
                  <Button onClick={() => { props.showState.setter(false) }} variant="cancel" className="w-100">
                    Cancel
                  </Button>
                </div>
                <div className="col-12 ps-2">
                  <Button type='submit' variant="primary" className="w-100">
                    Save
                  </Button>
                </div>
              </Row>
            </Container>
          </Form>
        )
      }}
    </Formik>
  )
}

export default ProviderServiceDetailsPage;
